<template>
  <header role="banner" class="app-header">
    <!-- Left Section -->
    <div class="header-left">
      <!-- Mobile Menu Button -->
      <BButton class="mobile-menu-btn"
              @click="toggleMobileSidebar"
              :aria-label="$t('Webportal.MenuOpen')">
        <Menu class="icon" :size="20" aria-hidden="true"></Menu>
      </BButton>

      <!-- Dashboard Title with Badge -->
      <div class="dashboard-title">
        <h1 class="title-text">
          {{ pageTitle }}
          <!-- <span class="status-badge">Live</span> -->
        </h1>
      </div>
    </div>

    <!-- Center Section - Search -->
    <div class="search-wrapper">
      <div class="search-container">
        <!-- ...existing search icon... -->
        <input  type="search"
                class="search-input"
                :placeholder="searchPlaceholder"
                v-model="searchQuery"
                @focus="handleSearchFocus"
                @click="handleSearchFocus"
                readonly
                      :disabled="true"

                :aria-label="$t('Webportal.Search')" />
        <!-- ...existing shortcuts... -->
      </div>
    </div>

    <!-- Right Section - Controls -->
    <div class="header-right">
      <!-- Language Selector - Bootstrap Vue Next Dropdown -->
      <BDropdown  variant="light"
                  size="sm"
                  class="language-dropdown"
                  no-caret
                  :disabled="isLoadingLocalizations"
                  right>
        <template #button-content>
          <Languages class="icon" aria-hidden="true" />
          <Wrench v-if="currentLocalization && currentLocalization[0].localizationTextId === 'deflt'" class="item-icon" aria-hidden="true" :size="16" />
          <span v-else class="flag-icon" :class="[`flag-icon-${getRegion(currentLocalization, false)}`]"></span>
          <ChevronDown class="chevron-icon" aria-hidden="true" size="16" />
        </template>
        <BDropdownHeader>{{ $t('Webportal.SelectLanguage') }}</BDropdownHeader>
        <BDropdownItem  v-for="localization in localizations"
                        :key="localization[0].localizationTextId"
                        :active="currentLocalization && currentLocalization[0].localizationTextId === localization[0].localizationTextId"
                        @click="changeLanguage(localization[0].localizationTextId)">
          <Wrench v-if="localization[0].localizationTextId === 'deflt'" class="item-icon" aria-hidden="true" :size="16" />
          <span v-else class="flag-icon" :class="[`flag-icon-${getRegion(localization, false)}`]"></span>
          <span class="language-name">{{ getLanguageDescription(localization) }}</span>
          <span v-if="currentLocalization && currentLocalization[0].localizationTextId === localization[0].localizationTextId" class="language-check">
            <Check :size="16" aria-hidden="true" />
          </span>
        </BDropdownItem>
      </BDropdown>

      <!-- Theme Toggle -->
      <BButton  variant="light"
                class="control-btn icon-only btn-no-border theme-toggle"
                @click="toggleTheme"
                size="sm"
                :aria-label="themeLabel">
        <Sun v-if="!isDarkMode" class="icon" :size="16" />
        <Moon v-else class="icon" :size="16" />
      </BButton>

      <!-- Notifications -->
      <BDropdown  variant="light"
                  size="sm"
                  class="notification-dropdown"
                  menu-class="notifications-menu"
                  toggle-class="control-btn icon-only notification-btn"
                  no-caret
                  right>
        <template #button-content>
          <Bell class="icon" aria-hidden="true" :size="12"/>
          <div v-if="unreadNotifications > 0" class="notification-badge">
            <span class="badge-count">{{ unreadNotifications }}</span>
          </div>
        </template>

        <BDropdownHeader>
          <div class="dropdown-header">
            <h3>{{ $t('Webportal.Notifications') }}</h3>
            <span class="badge-count-text">{{ unreadNotifications }} {{ $t('Webportal.Unread') }}</span>
          </div>
        </BDropdownHeader>

        <BDropdownDivider />

        <div class="notifications-list">
          <BDropdownItem
            v-for="notification in notifications"
            :key="notification.id"
            class="notification-item">
            <div class="notification-content">
              <p class="notification-title">{{ notification.title }}</p>
              <p class="notification-time">{{ notification.time }}</p>
            </div>
          </BDropdownItem>
        </div>
      </BDropdown>

      <!-- User Profile -->
      <BDropdown  variant="light"
                  class="user-dropdown"
                  menu-class="user-menu"
                  toggle-class="user-profile-btn"
                  no-caret
                  right>
        <template #button-content>
          <div class="user-info">
            <div class="user-name">{{ userDisplayName }}</div>
            <div class="user-company">{{ companyName }}</div>
          </div>
          <span class="user-avatar">
            <img  v-if="avatarImageLoaded"
                  class="avatar-image"
                  :alt="`Profile pincture of ${userDisplayName}`"
                  :src="userAvatarUrl"
                  @error="handleAvatarError" />
            <span v-else class="avatar-initials">{{ userInitials }}</span>
          </span>
          <ChevronDown class="chevron-icon" aria-hidden="true" :size="16" />
        </template>

        <BDropdownHeader>
          <div class="dropdown-header user-header">
            <span class="dropdown-avatar">
              <img
                v-if="avatarImageLoaded"
                class="avatar-image"
                :alt="`Profile pincture of ${userDisplayName}`"
                :src="userAvatarUrl"
                @error="handleAvatarError"
              />
              <span v-else class="avatar-initials">{{ userInitials }}</span>
            </span>
            <div class="dropdown-user-info">
              <div class="dropdown-user-name">{{ userDisplayName }}</div>
              <div class="dropdown-user-company">{{ companyName }}</div>
            </div>
          </div>
        </BDropdownHeader>

        <BDropdownDivider />

        <BDropdownItem @click="goToProfile">
          <User class="item-icon" aria-hidden="true" :size="24" />
          <span>{{ $t('Webportal.Profile') }}</span>
        </BDropdownItem>

        <BDropdownItem v-if="isMSMUser" @click="goToSettings">
          <Settings class="item-icon" aria-hidden="true" :size="12" />
          <span>{{ $t('Webportal.Settings') }}</span>
        </BDropdownItem>

        <BDropdownDivider />

        <BDropdownItem @click="showHelpGuide = !showHelpGuide">
          <FileQuestionMark class="item-icon" aria-hidden="true" :size="12" />
          <span>{{ $t('Webportal.Help') }}</span>
        </BDropdownItem>

        <BDropdownDivider />
        <BDropdownItem @click="logout" class="logout-item">
          <LogOut class="item-icon" aria-hidden="true" :size="16" />
          <span>{{ $t('Webportal.SignOut') }}</span>
        </BDropdownItem>
      </BDropdown>
    </div>

    <!-- Help Guide Component -->
    <Guide v-if="showHelpGuide" />

    <!-- Command Palette Dialog -->
    <CommandPalette v-model="showCommandPalette" />
  </header>
</template>

<script>
import CommandPalette from '@/componentsv3/CommandPalette.vue';
import { Check, Search, ChevronDown, Bell, Settings,
  User, LogOut, Sun, Moon,
  Menu, Languages, Flag, Wrench, FileQuestionMark } from 'lucide-vue-next';
import { mapGetters, mapActions } from 'vuex';
import { BDropdown, BDropdownItem, BDropdownDivider } from 'bootstrap-vue-next';
import AppConfig from "@/mixins/AppConfig-APP_TARGET";
import Guide from '@/components/Guide.vue';
import EventBus from "@/services/EventBus.js";

export default {
  name: 'AppHeaderAlternative',

  mixins: [AppConfig],

  components: {
    CommandPalette,
    Check,
    Search,
    ChevronDown,
    Bell,Settings, User,LogOut,Wrench,FileQuestionMark,
    Sun,Menu,Flag,
    Guide,
    Moon,
    Languages,
    BDropdown,
    BDropdownItem,
    BDropdownDivider
  },

  data() {
    return {
      searchQuery: '',
      showCommandPalette: false,
      showHelpGuide: false,
      notifications: [
        { id: 1, title: 'Neue Benachrichtigung', time: 'vor 5 Minuten' },
        { id: 2, title: 'System-Update', time: 'vor 1 Stunde' },
      ],
      unreadNotifications: 1,
      userAvatarUrl: undefined, // '/_assets/v11/50210ef4f0c7a99345ecbd7b6946c51ad23f24a8.png',
      avatarImageLoaded: false
    }
  },

  computed: {
    ...mapGetters('app', ['isDarkMode']),
    ...mapGetters('localization', ['localizations', 'currentLocalization', 'isLoadingLocalizations' ]),
    ...mapGetters('auth', [
      'clientId', 'isAuthenticated', 'isMSMUser', 'userProfile',
      'userDisplayName', 'userInitials']),
    ...mapGetters('clientData', ['structure']),

    companyName() {
      return this.structure?.label || 'Company';
    },

    // Generiere Hintergrundfarbe basierend auf Namen (konsistent)
    avatarBackgroundColor() {
      const colors = [
        '#EF4444', // red
        '#F59E0B', // amber
        '#10B981', // green
        '#3B82F6', // blue
        '#6366F1', // indigo
        '#8B5CF6', // violet
        '#EC4899', // pink
        '#14B8A6', // teal
      ];

      // Hash-Funktion für konsistente Farbzuweisung
      const name = this.userDisplayName;
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }

      const index = Math.abs(hash) % colors.length;
      return colors[index];
    },

    pageTitle() {
      if (this.$route.meta?.MessageKey)
        return this.$t(this.$route.meta?.MessageKey);
      else
        return this.$route.meta?.title || this.$route.name;
    },

    // Oder kombiniert:
    platform() {
      if (/Mac|iPhone|iPod|iPad/i.test(navigator.platform)) return 'mac';
      if (/Win/i.test(navigator.platform)) return 'windows';
      if (/Linux/i.test(navigator.platform)) return 'linux';
      return 'unknown';
    },

    searchPlaceholder() {
      const key = this.platform === 'mac' ? '⌘K' : 'Ctrl+K';
      return `${this.$t('Webportal.Search')}… (${key} ${this.$t('Webportal.CX.for')} ${this.$t('Webportal.CommandPalette')})`;
    },

    themeLabel() {
      return this.isDarkMode ? this.$t('Webportal.SwitchToLightMode') : this.$t('Webportal.SwitchToDarkMode');
    },

    // avatarImageLoaded() {
    //   // Einfache Prüfung: Wenn URL existiert, versuche zu laden
    //   return !!this.userAvatarUrl;
    // }
  },

  methods: {
    ...mapActions('app', ['toggleDarkMode', 'toggleMobileSidebar']),
    ...mapActions('auth', ['initializeAuth', 'logout']),
    ...mapActions('clientData', ['fetchClients', 'fetchStructure', 'fetchFilters']),
    ...mapActions('localization', ['fetchLocalizations', 'updateCurrentLocalization']),

    handleSearchFocus() {
      this.showCommandPalette = true;
    },

    changeLanguage(langCode) {
      this.updateCurrentLocalization(langCode);
    },

    toggleTheme() {
      this.toggleDarkMode();
    },

    goToProfile() {
      this.$router.push('/user/profile');
    },

    goToSettings() {
      this.$router.push('/administration/clientsettings');
    },

    handleGlobalKeydown(event) {
      if (APP_TARGET === 'lexus') return;
      if (APP_TARGET === 'demov3') return;
      const isMac = this.platform === 'mac';
      const modifierKey = isMac ? event.metaKey : event.ctrlKey;

      if (modifierKey && event.key === 'k') {
        event.preventDefault();
        this.showCommandPalette = true;
      }
    },

    handleAvatarError() {
      this.avatarImageLoaded = false;
    },

    getLanguageName(localization, languageName = LanguageName.Full) {
      if (!localization) return "";

      if (languageName === LanguageName.Full)
        if (localization.length > 1)
          return `${localization[0].languageName} - ${localization[1].languageName}`;
        else return localization[0].languageName;
      else if (languageName === LanguageName.Normal)
        return localization[0].languageName;
      else return localization[0].languageNameShort;
    },

    getLanguageDescription(localization) {
      if (!localization) return "unknown";
      let desc = localization[0].languageNameShort;
      if (localization.length > 1) {
        desc += `, ${localization[1].languageName}`;
        return desc;
      }
      return localization[0].languageName;
      return desc;
    },

    getRegion(localization, isHeader) {
      if (!localization || !localization[0].localizationTextId.includes("-"))
        return "";
      else {
        if (isHeader)
          console.error(localization, ' => ', localization[0].localizationTextId.split("-")[1].toLowerCase());
        return localization[0].localizationTextId.split("-")[1].toLowerCase();
      }
    },

  },

  async created() {
    await this.fetchClients();
    await this.fetchFilters();
    await this.initializeAuth();

    await Promise.all([
      this.fetchLocalizations(),
      this.fetchStructure()
    ]);
  },

  mounted() {
    window.addEventListener('keydown', this.handleGlobalKeydown);

    // Avatar-Bild vorladen
    if (this.userAvatarUrl) {
      const img = new Image();
      img.onload = () => { this.avatarImageLoaded = true; };
      img.onerror = () => { this.avatarImageLoaded = false; };
      img.src = this.userAvatarUrl;
    }

    EventBus.$on("GuideClosed", () => { this.showHelpGuide = false; });
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    EventBus.$off("GuideClosed");
  }
}
</script>

<style lang="scss" scoped>
.theme-toggle {
  background: none !important;
  color: var(--color-foreground) !important;
}
.btn-no-border {
  border: 0px !important;
  box-shadow: none !important;
}
.app-header {
  background-color: var(--bs-body-bg);
  backdrop-filter: blur(40px);
  border-bottom: 1px solid var(--bs-border-color);
  padding: 8px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 50;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  @media (max-width: 991px) {
    padding: 12px 16px;
  }
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.mobile-menu-btn {
  display: block;
  padding: 8px;
  color: var(--color-foreground);
  background: none;
  border: none;
  cursor: pointer;
  border-radius: var(--radius-md);
  transition: all 0.15s;

  &:hover {
    color: var(--color-background);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (min-width: 1025px) {
    display: none;
  }

  .material-symbols-outlined {
    font-size: 20px;
  }
}

.dashboard-title {
  .title-text {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    color: var(--color-foreground);
    font-size: 16px;
    line-height: 1.5;
    margin: 0;

    @media (min-width: 640px) {
      font-size: 18px;
    }

    @media (min-width: 992px) {
      font-size: 20px;
    }
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  background-color: #10b981;
  color: white;
  font-size: 12px;
  font-weight: 500;
  border-radius: var(--radius-sm);
  line-height: 1.5;
}

.search-wrapper {
  flex: 1;
  max-width: 512px;
  margin: 0 32px;

  @media (max-width: 991px) {
    display: none;
  }
}

.search-container {
  position: relative;
  width: 100%;

  .search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    color: var(--color-muted-foreground);
    transition: color 0.2s;
    pointer-events: none;
  }

  &:focus-within .search-icon {
    color: #f50537;
  }
}

.search-input {
  width: 100%;
  height: 40px;
  padding: 8px 112px 8px 44px;
  background-color: var(--bs-secondary-bg);
  border: 0;
  border-radius: var(--radius-md);
  font-size: 14px;
  color: var(--color-foreground);
  outline: none;
  transition: all 0.15s;
  cursor: pointer;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    background-color: white;
    box-shadow: 0 0 0 3px rgba(245, 5, 55, 0.1),
                0 4px 6px -1px rgba(0, 0, 0, 0.1);
    cursor: text;
  }
}

.screen-reader-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.search-shortcuts {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  pointer-events: none;
}

.kbd-key {
  padding: 4px 6px;
  background-color: #e5e7eb;
  border-radius: var(--radius-xs);
  font-size: 12px;
  color: var(--color-muted-foreground);
  font-family: monospace;
  line-height: 1;
}

.header-right {
  display: flex;
  align-items: center;
  color: var(--color-foreground);
  gap: 12px;
}

// .control-btn {
//   position: relative;
//   display: inline-flex;
//   align-items: center;
//   justify-content: center;
//   gap: 8px;
//   height: 36px;
//   padding: 0 12px;
//   background-color: #f9fafb;
//   // border: 1px solid transparent;
//   border-radius: var(--radius-md);
//   font-size: 14px;
//   font-weight: 500;
//   color: var(--color-foreground);
//   cursor: pointer;
//   transition: all 0.15s;
//   white-space: nowrap;

//   &:hover {
//     background-color: #e5e7eb;
//   }

//   &:active {
//     // transform: scale(0.98);
//   }

//   &:focus-visible {
//     // outline: 2px solid #f50537;
//     // outline-offset: 2px;
//     // box-shadow: 0 0 0 4px rgba(245, 5, 55, 0.2);
//   }

//   .icon {
//     width: 16px;
//     height: 16px;
//     flex-shrink: 0;
//   }

//   &.icon-only {
//     width: 36px;
//     padding: 0;

//     .icon {
//       width: 20px;
//       height: 20px;
//     }
//   }
// }

.language-btn {
  .language-code {
    font-size: 12px;
    font-weight: 600;
  }

  .chevron-icon {
    width: 12px;
    height: 12px;
    color: #9ca3af;
  }
}

.notification-btn {
  .notification-badge {
    position: absolute;
    top: -4px;
    right: -4px;

    .badge-count {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      background-color: var(--default-status-red);
      color: white;
      font-size: 10px;
      font-weight: 600;
      border-radius: var(--radius-full);
      border: 2px solid white;
    }
  }
}

.user-profile-btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  height: 44px;
  padding: 4px 12px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.15s;
  position: relative;

  &:hover {
    background-color: #f9fafb;
  }

  &:active {
    transform: scale(0.98);
  }

  .user-info {
    display: none;
    text-align: right;

    @media (min-width: 992px) {
      display: block;
    }

    .user-name {
      font-weight: 500;
      color: var(--color-foreground);
      font-size: 14px;
      line-height: 1.5;
    }

    .user-company {
      font-size: 12px;
      color: var(--color-muted-foreground);
      line-height: 1.5;
    }
  }

  .user-avatar {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    overflow: hidden;
    border-radius: var(--radius-full);
    border: 1px solid rgba(229, 231, 235, 0.5);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    background-color: v-bind(avatarBackgroundColor);

    .avatar-image {
      aspect-ratio: 1;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .avatar-initials {
      font-size: 14px;
      font-weight: 600;
      color: white;
      user-select: none;
      line-height: 1;
    }
  }

  .chevron-icon {
    width: 14px;
    height: 14px;
    color: #9ca3af;
    flex-shrink: 0;

    @media (max-width: 991px) {
      display: none;
    }
  }
}

// Dropdown Menu Styles
// .dropdown-menu {
//   position: absolute;
//   top: calc(100% + 8px);
//   right: 0;
//   background: white;
//   border: 1px solid var(--color-border);
//   border-radius: var(--radius-md);
//   box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
//               0 4px 6px -2px rgba(0, 0, 0, 0.05);
//   min-width: 200px;
//   z-index: 1000;
//   animation: fadeInDown 0.15s ease-out;

//   @keyframes fadeInDown {
//     from {
//       opacity: 0;
//       transform: translateY(-8px);
//     }
//     to {
//       opacity: 1;
//       transform: translateY(0);
//     }
//   }
// }

.dropdown-header {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;

  h3 {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-foreground);
    margin: 0;
  }

  .badge-count-text {
    font-size: 12px;
    color: var(--color-muted-foreground);
  }

  &.user-header {
    padding: 16px;
  }
}

.dropdown-avatar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: var(--radius-full);
  background-color: v-bind(avatarBackgroundColor);

  .avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-initials {
    font-size: 18px;
    font-weight: 600;
    color: white;
    user-select: none;
    line-height: 1;
  }
}

// Dark Mode Anpassungen
:root.dark,
[data-bs-theme="dark"] {
  .user-avatar,
  .dropdown-avatar {
    border-color: rgba(51, 65, 85, 0.5);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
}

.dropdown-user-info {
  flex: 1;

  .dropdown-user-name {
    font-weight: 500;
    color: var(--color-foreground);
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 2px;
  }

  .dropdown-user-company {
    font-size: 12px;
    color: var(--color-muted-foreground);
    line-height: 1.5;
  }
}

.dropdown-divider {
  height: 1px;
  background-color: #e5e7eb;
  margin: 0;
  border: none;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s;
  color: var(--color-foreground);
  font-size: 14px;
  font-weight: 500;
  text-align: left;

  &:hover {
    background-color: var(--color-accent) !important;
  }

  &:active {
    background-color: #f3f4f6;
  }

  &.active {
    background-color: #fef2f2;
    color: #f50537;
  }

  &.logout-item {
    color: var(--default-status-red);

    .item-icon {
      color: var(--default-status-red);
    }
  }

  .item-icon {
    width: 18px;
    height: 18px;
    color: var(--color-muted-foreground);
    flex-shrink: 0;
  }

  &:first-child {
    border-radius: var(--radius-md) var(--radius-md) var(--radius-none) var(--radius-none);
  }

  &:last-child {
    border-radius: var(--radius-none) var(--radius-none) var(--radius-md) var(--radius-md);
  }
}

.user-dropdown {
  @media (min-width: 992px) {
    min-width: 144px;
  }
  color: var(--color-foreground);
}

.notifications-dropdown {
  min-width: 320px;
  max-width: 400px;
}

.notifications-list {
  max-height: 400px;
  overflow-y: auto;
}

.notification-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: var(--color-accent) !important; // var(--bs-secondary-bg);
  }

  .notification-content {

    &:hover {
      background-color: var(--color-accent) !important; // var(--bs-secondary-bg);
    }

    .notification-title {
      font-size: 14px;
      font-weight: 500;
      color: var(--color-foreground);
      margin: 0 0 4px 0;
    }

    .notification-time {
      font-size: 12px;
      color: var(--color-muted-foreground);
      margin: 0;
    }
  }
}

.material-symbols-outlined {
  font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 20;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

// Bootstrap Dropdown Overrides
:deep(.language-dropdown),
:deep(.notification-dropdown),
:deep(.user-dropdown) {
  .dropdown-toggle {
    // Reset Bootstrap styles
    &::after {
      display: none !important;
    }

    color: var(--color-foreground);

    border: none !important;
    padding: 0 !important;
    background: transparent !important;
    box-shadow: none !important;

    &:hover,
    &:focus,
    &:active {
      background: transparent !important;
      columns: green;
      border: none !important;
    }
  }
}

// Language Dropdown Styles
:deep(.language-dropdown) {
  .control-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 12px;
    background-color: var(--color-accent-bg);
    border: 0px solid transparent;
    border-radius: var(--radius-md);
    font-size: 14px;
    font-weight: 500;
    color: var(--color-foreground);
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
      background-color: #e5e7eb;
    }

    &:active {
      transform: scale(0.98);
    }

    .icon {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }

    .language-code {
      font-size: 12px;
      font-weight: 600;
    }

    .chevron-icon {
      width: 12px;
      height: 12px;
      color: #9ca3af;
    }
  }
}

:deep(.language-menu) {
  min-width: 200px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
              0 4px 6px -2px rgba(0, 0, 0, 0.05);
  padding: 4px;
  margin-top: 8px;

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: var(--radius-sm);
    font-size: 14px;
    font-weight: 500;
    transition: background-color 0.15s;
    color: var(--color-foreground);

    .language-flag {
      font-size: 18px;
    }

    .language-name {
      flex: 1;
    }

    .language-check {
      display: flex;
      align-items: center;
      color: #f50537;
    }

    &:hover {
      background-color: var(--color-accent) !important;
    }

    &.active {
      background-color: #fef2f2;
      color: #f50537;
    }
  }
}

// Notification Dropdown Styles
:deep(.notification-dropdown) {
  .control-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    background-color: var(--color-accent);
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
      background-color: #e5e7eb;
    }

    .icon {
      width: 20px;
      height: 20px;
    }
  }

  .notification-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    pointer-events: none;

    .badge-count {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      background-color: var(--default-status-red);
      color: white;
      font-size: 10px;
      font-weight: 600;
      border-radius: var(--radius-full);
      border: 2px solid white;
    }
  }
}

:deep(.notifications-menu) {
  min-width: 320px;
  max-width: 400px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
              0 4px 6px -2px rgba(0, 0, 0, 0.05);
  padding: 0;
  margin-top: 8px;
  transition: background-color 0.15s;

  .dropdown-item:hover {
    background-color: var(--color-accent) !important;
  }

  .dropdown-header {
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    h3 {
      font-size: 14px;
      font-weight: 600;
      color: var(--color-foreground);
      margin: 0;
    }

    .badge-count-text {
      font-size: 12px;
      color: var(--color-muted-foreground);
    }
  }

  .notifications-list {
    max-height: 400px;
    overflow-y: auto;

    .notification-item {
      padding: 12px 16px;
      cursor: pointer;
      border: none;

      &:hover {
        background-color: var(--color-accent) !important;
      }

      .notification-content {
        &:hover {
          background-color: var(--color-accent) !important;
        }

        .notification-title {
          font-size: 14px;
          font-weight: 500;
          color: var(--color-foreground);
          margin: 0 0 4px 0;
        }

        .notification-time {
          font-size: 12px;
          color:  var(--color-muted-foreground);
          margin: 0;
        }
      }
    }
  }
}

// User Dropdown Styles
:deep(.user-dropdown) {
  .user-profile-btn {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    height: 44px;
    padding: 4px 12px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
      background-color: var(--color-accent);
    }

    &:active {
      transform: scale(0.98);
    }

    .user-info {
      display: none;
      text-align: right;

      @media (min-width: 992px) {
        display: block;
      }

      .user-name {
        font-weight: 500;
        color: var(--color-foreground);
        font-size: 14px;
        line-height: 1.5;
      }

      .user-company {
        font-size: 12px;
        color: var(--color-muted-foreground);
        line-height: 1.5;
      }
    }

    .user-avatar {
      position: relative;
      display: flex;
      width: 36px;
      height: 36px;
      flex-shrink: 0;
      overflow: hidden;
      border-radius: var(--radius-full);
      border: 1px solid rgba(229, 231, 235, 0.5);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

      .avatar-image {
        aspect-ratio: 1;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .chevron-icon {
      width: 14px;
      height: 14px;
      color: #9ca3af;
      flex-shrink: 0;

      @media (max-width: 991px) {
        display: block;
      }
    }
  }
}

:deep(.user-menu) {
  min-width: 280px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
              0 4px 6px -2px rgba(0, 0, 0, 0.05);
  padding: 0;
  margin-top: 8px;

  .dropdown-header.user-header {
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 12px;

    .dropdown-avatar {
      position: relative;
      display: flex;
      width: 48px;
      height: 48px;
      flex-shrink: 0;
      overflow: hidden;
      border-radius: var(--radius-full);

      .avatar-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .dropdown-user-info {
      flex: 1;

      .dropdown-user-name {
        font-weight: 500;
        color: var(--color-foreground);
        font-size: 14px;
        line-height: 1.5;
        margin-bottom: 2px;
      }

      .dropdown-user-company {
        font-size: 12px;
        color: var(--color-muted-foreground);
        line-height: 1.5;
      }
    }
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    font-size: 14px;
    font-weight: 500;
    color: var(--color-foreground);
    transition: background-color 0.15s;

    &:hover {
      background-color: var(--color-accent) !important;
    }

    &.logout-item {
      color: var(--default-status-red);

      .item-icon {
        color: var(--default-status-red);
      }
    }

    .item-icon {
      width: 18px;
      height: 18px;
      color: var(--color-muted-foreground);
      flex-shrink: 0;
    }
  }

}

</style>
